import { useContext, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { translateService } from "../../services/TranslateService";
import styles from "./main.module.scss";
import React from "react";
import { GlobalStore } from "../../reduxStore";
import { cx } from "./util";
import { DataContext, ISearchParameters } from "../../contexts/dataContext";
import { normalizeCouponCode } from "../../services/couponCode";

export default function Coupon({ carId, setSearchParams }: any) {
  const dataContext = useContext(DataContext);
  const apiService = useSelector((state: GlobalStore) => state.chauffeurReducer.apiService);
  const dispatch = useDispatch();
  const [localCouponCode, setLocalCouponCode] = useState(normalizeCouponCode(dataContext.couponCode));

  useEffect(() => {
    let urlCodeValid = sessionStorage.getItem('urlCodeValid') ?? undefined;
    if(urlCodeValid === 'false') {
      dataContext.changeCouponDescription(translateService.translate('vehicleSearch.couponWasNotValidMessage'));
      sessionStorage.removeItem('urlCodeValid');
    }
  }, [])

  useEffect(() => {
    // if code cleared at payment screen, the window reloaded --> vehicle object may need refresh if url code valid
    // if vehicle coupon data null, pricing will not update even if coupon is applied
    let pricingDataType = dataContext.noVatPrices ? dataContext.vehicle?.pricingDataNoVat : dataContext.vehicle?.pricingData;
    if(carId && dataContext.storedCoupon?.valid && localCouponCode && !pricingDataType?.coupon) {
      search((previousState: ISearchParameters) => ({ ...previousState, discount: localCouponCode }));
    }
  }, [carId, dataContext.storedCoupon?.valid, dataContext.vehicle?.pricingData.coupon, dataContext.vehicle?.pricingDataNoVat.coupon])

  const handleInvalidCode = () => {
    if(dataContext.route && dataContext.route === "/makereservation") { // if changing coupon on payment screen
      // these methods allows services and payment methods to update correctly and quicker
      if (setSearchParams) {
        setSearchParams((previousState: ISearchParameters) => ({ ...previousState, discount: null }))
      } else {
        search((previousState: ISearchParameters) => ({ ...previousState, discount: null }));
      }
    } else { // searching with coupon on Vehicle Search screen
      // only search again if latest results from valid coupon before the invalid coupon was applied --> resets results
      if (dataContext.searchParameters?.discount) {
        dataContext.changeSearchParameters({...dataContext.searchParameters, discount: null});
        // if no older coupon stored, removed invalid one without triggering reload
      }
      dataContext.changeValidatedVipVehicles(-1);
    }
  }

  const removeDiscountCode = () => {
    setLocalCouponCode('');
    dataContext.changeCouponDescription('');
    dataContext.changeUser({ ...dataContext.user, couponCode: '' });
    dataContext.changeCouponCode('');
    dataContext.changeStoredCoupon(null);
    dataContext.changeValidatedVipVehicles(-1);
    sessionStorage.removeItem("coupon");
    sessionStorage.setItem('urlCodeValid', 'removed');
    handleInvalidCode();
  }

  useEffect(() => {
    if(dataContext.searchParameters?.discount! && dataContext.searchParameters?.discount !== "") {
      setLocalCouponCode(normalizeCouponCode(dataContext.searchParameters?.discount));
    }
  }, []);

  const search = (p: any) => {
    let newParams = p(dataContext.searchParameters);
    if (carId) {
      let mergeParams = { ...newParams, carId: carId };
      apiService?.vehicle.searchWithParams(mergeParams, !!dataContext.loggedIn)
          .then(r => {
            if (r.count === 1) {
              if (r.vehicles[0].availabilityData.available) {
                dataContext.changeVehicle(r.vehicles[0])
              } else {
                dispatch({ type: 'SET_RESPONSE_MODAL', payload: translateService.translate("vehicleSearch1.notAvailable") })
                return;
              }
            }
            dataContext.changeSearchParameters(newParams);
          })
          .catch(e => {
            if (dataContext.appContext !== "production") console.log(e);
            dispatch({ type: 'SET_RESPONSE_MODAL', payload: translateService.translate('vehicleSearch.errorLoadingResults') });
          })
          .finally(() =>
              dispatch({ type: 'SET_API_MESSAGE', payload: undefined })
          );
    } else {
      // this search will be a quicker process + easier on BE if no car id has been selected. Older method (above) does search twice, #todo - change to make more efficient
      dataContext.changeSearchParameters(newParams);
    }
  }

  function getCoupon(autoApplyCoupon?: string) {
    // normalised so the same campaign code always reaches the API and GA4 as one string
    let codeToCheck = normalizeCouponCode(autoApplyCoupon ?? localCouponCode);
    if (codeToCheck === '') return;
    apiService?.vehicle.getCoupon(codeToCheck).then(r => {
        if (!r.valid) {
          dataContext.changeCouponDescription(translateService.translate('vehicleSearch.couponWasNotValidMessage'));
        } else {
          if (setSearchParams) {
            setSearchParams((previousState: ISearchParameters) => ({ ...previousState, discount: codeToCheck }))
          } else {
            search((previousState: ISearchParameters) => ({ ...previousState, discount: codeToCheck }));
            if (autoApplyCoupon) //don't show user profile set coupon description - don't want
                // to remind driver of negative experience coupon sought to remedy
              dataContext.changeCouponDescription(translateService.translate('vehicleSearch.couponAutoApplied'));
            else {
              dataContext.changeCouponDescription(r.description);
            }
          }
          dataContext.changeValidatedVipVehicles(-1); // clear VIP codes if any vehicles were previously unlocked (code will unlock again if needed)
        }
        dataContext.changeStoredCoupon({...r, code: codeToCheck})
        dataContext.changeCouponCode(codeToCheck);
      }).catch(e => {
        if (e.includes("NoCouponWithProvidedCode")) {
          dataContext.changeCouponDescription(translateService.translate('vehicleSearch.couponWasNotValidMessage'));
        }
        else {
          dataContext.changeCouponDescription(translateService.translate('vehicleSearch.errorInfo'));
        }
        handleInvalidCode();
        dataContext.changeStoredCoupon(null);
        dataContext.changeCouponCode('');
      })
  }

  useEffect(() => {
    const currentCouponCode = normalizeCouponCode(dataContext.user?.couponCode);
    if (currentCouponCode !== '' && currentCouponCode !== normalizeCouponCode(dataContext.couponCode)
        && sessionStorage.getItem('autoAppliedCoupon') !== '1' &&
        (dataContext.route !== "/makereservation" ||
            (dataContext.route === "/makereservation" && dataContext.couponCode === ''))) {
      // coupon auto apply should not be applied if already in payment screen + user already has discount code loaded
      setLocalCouponCode(currentCouponCode);
      sessionStorage.setItem("autoAppliedCoupon", '1');
      getCoupon(currentCouponCode);
    }
  }, [dataContext.user?.couponCode]);

  return <>
    <div className={styles.vehicleSearch}>
        <form onSubmit={e => e.preventDefault()}>
          <div className={cx(styles.r24FullWidthFlexCenter, styles.r24Padding10)} >
            <div className={cx(styles.r24ChildFlex1, styles.grayBorderAround)}>
              <input type="text"
                     disabled={!!dataContext.user?.couponCode}
                     className={cx(styles.discountInput)}
                     onChange={e => {const code = normalizeCouponCode(e.target.value);
                       setLocalCouponCode(code);
                       if (dataContext.couponDescription &&
                           dataContext.couponDescription === translateService.translate('vehicleSearch.couponWasNotValidMessage')
                           && dataContext.couponCode !== code) {
                         dataContext.changeCouponDescription('')}}}
                     value={localCouponCode}
                     id="discount"
                     placeholder={translateService.translate('vehicleSearch.discountcode')}/>
            </div>
            { !dataContext.user?.couponCode &&
                <div className={cx(styles.r24ChildFlex1, styles.r24LeftMargin5)}>
                  <button disabled={!localCouponCode || localCouponCode === ''}
                          className={"btn btn-primary btn-outline-primary btn-all w-100 font-weight-bold"}
                          onClick={() => {
                            if (dataContext.startDateInPast) {
                              // do not allow coupon to be checked if dates are in past (won't apply correctly)
                              dataContext.openModifyDates();
                              return;
                            }
                            getCoupon();
                            dataContext.sendGa4Event("apply_coupon", { coupon_result: "submit" });
                          }}
                          type='submit'>
                    {translateService.translate('vehicleSearch.getDiscount')}
                  </button>
                </div>}
          </div>
        </form>
      {dataContext.couponDescription && dataContext.couponDescription !== '' &&
          <div className={cx(styles.r24FullWidth, styles.r24TopMargin10, styles.r24BottomMargin20)}>
            <div className={cx(styles.r24FullWidth, styles.redBackground)}>
              {dataContext.storedCoupon?.valid ? // note: this is a hotfix --> somehow a coupon code came back valid: false but description is also getting set normally
                  dataContext.couponDescription : translateService.translate('vehicleSearch.couponWasNotValidMessage')}
          </div>
          <div className={'d-flex pt-1'}>
            <button className={"btn btn-primary btn-outline-primary btn-all w-50 ml-auto"}
                onClick={() => {removeDiscountCode()}}>
              {translateService.translate('vehicleSearch.removeDiscountCode')}
            </button>
          </div>
        </div>
      }
    </div>
  </>
}